import type {
  DiagnosticSeverity,
  DiagnosticTier,
  DiagnosticTriageStatus,
} from "admin/types/interfaces/diagnostics";

export const DIAGNOSTICS_API = "/api/conversations/v1/diagnostics";

// N.B. Badge colors below must stay within the design-system hues that
// `src/index.css` remaps and inverts under `.dark`: blue, gray, green, orange,
// purple, red, yellow. Those flip automatically in dark mode (`bg-red-100`
// becomes dark, `text-red-700` becomes light), so no `dark:` variants are
// needed. Raw Tailwind hues (amber, slate, indigo, zinc) have no dark mapping
// and would render as bright light-mode stickers on the nextgen dark theme.

/**
 * Severity badge configuration: colors and labels for each severity level.
 * Backend severity is `high | medium | low` (high = worst quality).
 */
export const SEVERITY_CONFIG = {
  high: {
    label: "High",
    badgeClass: "bg-red-100 text-red-700 border border-red-300",
  },
  medium: {
    label: "Medium",
    badgeClass: "bg-orange-100 text-orange-700 border border-orange-300",
  },
  low: {
    label: "Low",
    badgeClass: "bg-gray-100 text-gray-600 border border-gray-300",
  },
} as const satisfies Record<DiagnosticSeverity, { label: string; badgeClass: string }>;

/**
 * Tint for the in-session per-turn diagnostics indicator, which carries `unscored`
 * as a fourth state. Deliberately not `SEVERITY_CONFIG`: that map renders `low`
 * gray, which reads correctly in the dense triage table but is indistinguishable
 * from `unscored` when the icon is a turn's only diagnostics signal. Size and
 * shape are the indicator's own; only the tint varies by state.
 */
export const SEVERITY_INDICATOR_CONFIG = {
  high: {
    label: "High",
    iconButtonClass: "bg-red-100 text-red-600",
  },
  medium: {
    label: "Medium",
    iconButtonClass: "bg-orange-100 text-orange-600",
  },
  low: {
    label: "Low",
    iconButtonClass: "bg-green-100 text-green-600",
  },
  unscored: {
    label: "Not scored",
    iconButtonClass: "bg-muted text-muted-foreground",
  },
} as const satisfies Record<
  DiagnosticSeverity | "unscored",
  { label: string; iconButtonClass: string }
>;

/**
 * Tier badge configuration. `heuristics` turns are signals-only ($0);
 * `llm` turns carry full rubric scoring.
 */
export const TIER_CONFIG = {
  heuristics: {
    label: "Heuristics",
    badgeClass: "bg-gray-100 text-gray-600 border border-gray-300",
  },
  llm: {
    label: "LLM",
    badgeClass: "bg-purple-100 text-purple-700 border border-purple-300",
  },
} as const satisfies Record<DiagnosticTier, { label: string; badgeClass: string }>;

/**
 * Triage status options with display labels and Tailwind color classes.
 */
export const TRIAGE_STATUS_OPTIONS: {
  value: DiagnosticTriageStatus;
  label: string;
  badgeClass: string;
}[] = [
  {
    value: "unreviewed",
    label: "Unreviewed",
    badgeClass: "bg-gray-100 text-gray-700 border-gray-300",
  },
  {
    value: "reviewing",
    label: "Reviewing",
    badgeClass: "bg-blue-100 text-blue-700 border-blue-300",
  },
  {
    value: "ticketed",
    label: "Ticketed",
    badgeClass: "bg-purple-100 text-purple-700 border-purple-300",
  },
  {
    value: "wont_fix",
    label: "Won't Fix",
    badgeClass: "bg-red-100 text-red-700 border-red-300",
  },
  {
    value: "resolved",
    label: "Resolved",
    badgeClass: "bg-green-100 text-green-700 border-green-300",
  },
  {
    value: "no_action",
    label: "No Action",
    badgeClass: "bg-orange-100 text-orange-700 border-orange-300",
  },
  {
    value: "excluded",
    label: "Excluded",
    badgeClass: "bg-gray-100 text-gray-400 border-gray-300 italic",
  },
];

/**
 * Failure layer key to display name and Tailwind color mapping. These are the
 * rubric's `agent_layer` values - internal stages of the analysis pipeline, not
 * the user-facing Agents under `/nextgen/agents`.
 * "none" represents turns with no identified failure layer.
 */
export const FAILURE_LAYER_CONFIG = {
  orchestrator: {
    label: "Orchestrator",
    color: "#3b82f6",
    badgeClass: "bg-blue-100 text-blue-700",
  },
  data_retriever: {
    label: "Data Retriever",
    color: "#ef4444",
    badgeClass: "bg-red-100 text-red-700",
  },
  code_interpreter: {
    label: "Code Interpreter",
    color: "#f59e0b",
    badgeClass: "bg-orange-100 text-orange-700",
  },
  explore_agent: {
    label: "Explore Agent",
    color: "#10b981",
    badgeClass: "bg-green-100 text-green-700",
  },
  cross_cutting: {
    label: "Cross-Cutting",
    color: "#a855f7",
    badgeClass: "bg-purple-100 text-purple-700",
  },
  // Legacy label for pre-rename rows; the rubric renamed this layer to
  // `code_interpreter`, but older diagnostics persisted `python_analyzer`.
  python_analyzer: {
    label: "Code Interpreter",
    color: "#f59e0b",
    badgeClass: "bg-orange-100 text-orange-700",
  },
  none: {
    label: "None",
    color: "#94a3b8",
    badgeClass: "bg-gray-100 text-gray-600",
  },
} as const;

export type FailureLayerKey = keyof typeof FAILURE_LAYER_CONFIG;

export function getFailureLayerLabel(layerKey: string | null): string {
  if (!layerKey) return "None";
  const key = layerKey.toLowerCase() as FailureLayerKey;
  return FAILURE_LAYER_CONFIG[key]?.label ?? layerKey;
}

export function getFailureLayerBadgeClass(layerKey: string | null): string {
  if (!layerKey) return FAILURE_LAYER_CONFIG.none.badgeClass;
  const key = layerKey.toLowerCase() as FailureLayerKey;
  return FAILURE_LAYER_CONFIG[key]?.badgeClass ?? "bg-gray-100 text-gray-600";
}

export function getTriageStatusConfig(
  status: DiagnosticTriageStatus
): (typeof TRIAGE_STATUS_OPTIONS)[number] {
  return TRIAGE_STATUS_OPTIONS.find((opt) => opt.value === status) ?? TRIAGE_STATUS_OPTIONS[0];
}

/**
 * Severity filter options for the triage table dropdown.
 * "all" is a UI-only sentinel that maps to an omitted filter.
 */
export const SEVERITY_FILTER_OPTIONS = [
  { value: "all", label: "All Severities" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
] as const;

/**
 * Failure layer filter options for the triage table dropdown (API key values,
 * sent as `primary_failure_agent`). Not the session's Agent - see
 * `FAILURE_LAYER_CONFIG`.
 * "all" is a UI-only sentinel that maps to an omitted filter.
 */
export const FAILURE_LAYER_FILTER_OPTIONS = [
  { value: "all", label: "All Layers" },
  { value: "orchestrator", label: "Orchestrator" },
  { value: "data_retriever", label: "Data Retriever" },
  { value: "code_interpreter", label: "Code Interpreter" },
  { value: "explore_agent", label: "Explore Agent" },
  { value: "cross_cutting", label: "Cross-Cutting" },
] as const;

/**
 * Tier filter options for the triage table dropdown.
 * "all" is a UI-only sentinel that maps to an omitted filter.
 */
export const TIER_FILTER_OPTIONS = [
  { value: "all", label: "All Tiers" },
  { value: "llm", label: "LLM" },
  { value: "heuristics", label: "Heuristics" },
] as const;

export const TRIAGE_SORT_KEYS = {
  CREATED_AT: "created_at",
  SEVERITY: "severity",
  CONCERN_SCORE: "concern_score",
} as const;

export type TriageSortKey = (typeof TRIAGE_SORT_KEYS)[keyof typeof TRIAGE_SORT_KEYS];
